import { defaultRulePattern, formatSEK, isIncomingSwish } from '@budgie/shared';
import { memo, useId, useMemo, useState, useSyncExternalStore } from 'react';
import { useStore } from 'zustand';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import type { ImportedTxn, ImportReviewStoreApi } from '../../lib/state/importReviewStore';
import type { TxnOverride } from '../../routes/import/-rule-application';
import { PatternSelector } from './PatternSelector';

type Category = { id: string; label: string };
type Group = { id: string; name: string };

interface ImportPreviewTableProps {
  store: ImportReviewStoreApi;
  categories: Category[];
  groups: Group[];
  onCreateCategory?: (groupId: string, name: string) => Promise<{ id: string } | null>;
}

const MD_QUERY = '(min-width: 768px)';

function subscribeMd(onChange: () => void) {
  const mql = window.matchMedia(MD_QUERY);
  mql.addEventListener('change', onChange);
  return () => mql.removeEventListener('change', onChange);
}

function getMdSnapshot() {
  return window.matchMedia(MD_QUERY).matches;
}

/** True at the `md` breakpoint and up (table layout); false on phones (card layout). */
function useIsMdUp() {
  return useSyncExternalStore(subscribeMd, getMdSnapshot, () => true);
}

function confidenceColor(confidence: number) {
  if (confidence >= 80) return 'border-transparent bg-positive-soft text-positive';
  if (confidence >= 50) return 'border-transparent bg-warning-soft text-warning';
  if (confidence > 0) return 'border-transparent bg-negative-soft text-negative';
  return 'text-muted-foreground';
}

function confidenceLabel(confidence: number) {
  if (confidence >= 80) return 'Hög';
  if (confidence >= 50) return 'Medel';
  if (confidence > 0) return 'Låg';
  return 'Ingen';
}

export const ImportPreviewTable = memo(function ImportPreviewTable({
  store,
  categories,
  groups,
  onCreateCategory,
}: ImportPreviewTableProps) {
  const transactions = useStore(store, (state) => state.transactions);
  const isMdUp = useIsMdUp();
  const [creatingFor, setCreatingFor] = useState<string | null>(null);
  const [patternForTxnId, setPatternForTxnId] = useState<string | null>(null);
  const [newCatGroupId, setNewCatGroupId] = useState('');
  const [newCatName, setNewCatName] = useState('');
  const [saving, setSaving] = useState(false);

  const handleCancelCreate = () => {
    setCreatingFor(null);
    setNewCatName('');
    setNewCatGroupId('');
  };

  const handleSaveCreate = async (txnId: string) => {
    if (!onCreateCategory || !newCatGroupId || !newCatName.trim()) return;
    setSaving(true);
    const result = await onCreateCategory(newCatGroupId, newCatName.trim());
    setSaving(false);
    if (result) {
      const override = store.getState().overrides[txnId];
      if (!override) return;
      store.getState().setTransactionOverride(txnId, {
        ...override,
        categoryId: result.id,
        isManualOverride: true,
        autoAppliedRulePattern: null,
      });
      setCreatingFor(null);
      setNewCatName('');
      setNewCatGroupId('');
    }
  };

  const rowProps = (transaction: ImportedTxn): ImportPreviewRowProps => ({
    store,
    transaction,
    categories,
    groups,
    creatingFor,
    setCreatingFor,
    newCatGroupId,
    setNewCatGroupId,
    newCatName,
    setNewCatName,
    saving,
    handleSaveCreate,
    handleCancelCreate,
    onOpenPattern: setPatternForTxnId,
    onCreateCategory,
  });

  const patternSelector = patternForTxnId && (
    <PatternSelectorBridge
      store={store}
      transactions={transactions}
      patternForTxnId={patternForTxnId}
      onCancel={() => setPatternForTxnId(null)}
      onConfirm={(pattern) => {
        const current = store.getState().overrides[patternForTxnId];
        if (!current) {
          setPatternForTxnId(null);
          return;
        }

        store.getState().setTransactionOverride(patternForTxnId, {
          ...current,
          createRule: true,
          rulePattern: pattern,
        });
        setPatternForTxnId(null);
      }}
    />
  );

  if (!isMdUp) {
    return (
      <div className="space-y-2">
        {transactions.map((transaction) => (
          <ImportPreviewCard key={transaction.id} {...rowProps(transaction)} />
        ))}
        {patternSelector}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-2xl border border-border bg-card">
      <Table className="min-w-225">
        <TableHeader>
          <TableRow className="border-border bg-muted/60 text-xs uppercase text-muted-foreground hover:bg-muted/60">
            <TableHead className="py-1.5 pl-4 text-left font-medium">Datum</TableHead>
            <TableHead className="py-1.5 px-2 text-left font-medium">Beskrivning</TableHead>
            <TableHead className="py-1.5 px-2 text-right font-medium">Belopp</TableHead>
            <TableHead className="py-1.5 px-2 text-center font-medium">Konf.</TableHead>
            <TableHead className="py-1.5 px-2 text-left font-medium">Kategori</TableHead>
            <TableHead className="py-1.5 px-2 text-center font-medium">Regel</TableHead>
            <TableHead className="py-1.5 px-2 text-center font-medium">Överf.</TableHead>
            <TableHead className="py-1.5 pr-4 text-center font-medium">Hoppa</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {transactions.map((transaction) => (
            <ImportPreviewRow key={transaction.id} {...rowProps(transaction)} />
          ))}
        </TableBody>
      </Table>

      {patternSelector}
    </div>
  );
});

interface ImportPreviewRowProps {
  store: ImportReviewStoreApi;
  transaction: ImportedTxn;
  categories: Category[];
  groups: Group[];
  creatingFor: string | null;
  setCreatingFor: (value: string | null) => void;
  newCatGroupId: string;
  setNewCatGroupId: (value: string) => void;
  newCatName: string;
  setNewCatName: (value: string) => void;
  saving: boolean;
  handleSaveCreate: (txnId: string) => Promise<void>;
  handleCancelCreate: () => void;
  onOpenPattern: (txnId: string | null) => void;
  onCreateCategory: ((groupId: string, name: string) => Promise<{ id: string } | null>) | undefined;
}

// ── Shared row logic (used by both the desktop table row and the mobile card) ──

/**
 * Reads a transaction's override from the store and returns the handlers that
 * mutate it. Both layouts use this so behaviour stays identical.
 */
function useImportRowControls({
  store,
  transaction,
  groups,
  setCreatingFor,
  setNewCatGroupId,
  setNewCatName,
  onOpenPattern,
}: ImportPreviewRowProps) {
  const override = useStore(store, (state) => state.overrides[transaction.id]);
  const setTransactionOverride = useStore(store, (state) => state.setTransactionOverride);

  if (!override) return null;

  const update = (patch: Partial<TxnOverride>) =>
    setTransactionOverride(transaction.id, { ...override, ...patch });

  return {
    override,
    onCategoryChange: (value: string) => {
      if (value === '__create__') {
        setCreatingFor(transaction.id);
        setNewCatGroupId(groups[0]?.id ?? '');
        setNewCatName('');
        return;
      }

      update({
        categoryId: value === '__uncategorized__' ? null : value,
        isManualOverride: true,
        autoAppliedRulePattern: null,
      });
    },
    onCreateRuleChange: (checked: boolean | 'indeterminate') => {
      if (checked === true) {
        onOpenPattern(transaction.id);
        return;
      }
      update({ createRule: false });
    },
    onOpenPattern: () => onOpenPattern(transaction.id),
    onTransferChange: (checked: boolean | 'indeterminate') => {
      const isTransfer = checked === true;
      update({
        isTransfer,
        createTransferRule: isTransfer && override.createTransferRule,
        createRule: isTransfer ? false : override.createRule,
        rulePattern: isTransfer ? null : override.rulePattern,
        autoAppliedRulePattern: isTransfer ? null : override.autoAppliedRulePattern,
      });
    },
    onCreateTransferRuleChange: (checked: boolean | 'indeterminate') =>
      update({ createTransferRule: checked === true }),
    onSkipChange: (checked: boolean | 'indeterminate') =>
      update({
        skip: checked === true,
        autoAppliedRulePattern: checked === true ? null : override.autoAppliedRulePattern,
      }),
  };
}

type RowControls = NonNullable<ReturnType<typeof useImportRowControls>>;

function rowStateClass(override: TxnOverride, transaction: ImportedTxn) {
  if (override.skip) return 'opacity-40 bg-muted/50';
  if (override.autoAppliedRulePattern) return 'bg-primary/5 hover:bg-primary/10';
  if (transaction.autoConfirmed) return 'opacity-60 bg-positive-soft/60';
  return 'hover:bg-muted/40';
}

function amountClass(amount: number) {
  return amount < 0 ? 'text-foreground' : 'text-positive';
}

function TransactionBadges({
  transaction,
  override,
}: {
  transaction: ImportedTxn;
  override: TxnOverride;
}) {
  return (
    <>
      {transaction.isDuplicate && (
        <Badge variant="outline" className="ml-1 border-transparent bg-warning-soft text-warning">
          dubblett
        </Badge>
      )}
      {transaction.isTransfer && (
        <Badge variant="outline" className="ml-1 border-transparent bg-muted text-foreground">
          överföring
        </Badge>
      )}
      {transaction.autoConfirmed && (
        <Badge variant="outline" className="ml-1 border-transparent bg-positive-soft text-positive">
          auto
        </Badge>
      )}
      {override.autoAppliedRulePattern && (
        <Badge variant="outline" className="ml-1 border-transparent bg-primary/10 text-primary">
          regel
        </Badge>
      )}
    </>
  );
}

function ConfidenceBadge({ transaction }: { transaction: ImportedTxn }) {
  const hint = `Konfidens: ${confidenceLabel(transaction.confidence)} (${transaction.confidence}%)${transaction.matchedRuleId ? ' — matchad regel' : ''}`;
  return (
    <Badge
      variant="outline"
      className={`inline-flex gap-1 ${confidenceColor(transaction.confidence)}`}
      title={hint}
    >
      {transaction.confidence > 0 ? `${transaction.confidence}%` : '—'}
    </Badge>
  );
}

/** Category picker, or the inline "new category" form for this transaction. */
function CategoryControl({
  props,
  controls,
  layout,
}: {
  props: ImportPreviewRowProps;
  controls: RowControls;
  layout: 'table' | 'card';
}) {
  const {
    transaction,
    categories,
    groups,
    creatingFor,
    newCatGroupId,
    setNewCatGroupId,
    newCatName,
    setNewCatName,
    saving,
    handleSaveCreate,
    handleCancelCreate,
    onCreateCategory,
  } = props;
  const { override } = controls;
  const isCard = layout === 'card';

  if (creatingFor === transaction.id) {
    return (
      <div className={isCard ? 'flex flex-wrap items-center gap-2' : 'flex items-center gap-1'}>
        <Select value={newCatGroupId} onValueChange={setNewCatGroupId}>
          <SelectTrigger
            aria-label="Kategorigrupp"
            className={isCard ? 'min-w-0 flex-1 data-[size=default]:h-11' : 'h-7 w-24 text-xs'}
            size={isCard ? 'default' : 'sm'}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {groups.map((group) => (
              <SelectItem key={group.id} value={group.id}>
                {group.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          type="text"
          value={newCatName}
          onChange={(event) => setNewCatName(event.target.value)}
          placeholder="Namn…"
          aria-label="Namn på ny kategori"
          className={isCard ? 'h-11 min-w-0 flex-1' : 'h-7 w-24 text-xs'}
          onKeyDown={(event) => {
            if (event.key === 'Enter') void handleSaveCreate(transaction.id);
            if (event.key === 'Escape') handleCancelCreate();
          }}
        />
        <div className="flex items-center gap-1">
          <Button
            type="button"
            onClick={() => void handleSaveCreate(transaction.id)}
            disabled={saving || !newCatName.trim()}
            variant="ghost"
            size="icon"
            className="size-11 text-positive hover:text-positive/80 md:size-8"
            title="Spara"
            aria-label="Spara ny kategori"
          >
            ✓
          </Button>
          <Button
            type="button"
            onClick={handleCancelCreate}
            variant="ghost"
            size="icon"
            className="size-11 text-muted-foreground hover:text-foreground md:size-8"
            title="Avbryt"
            aria-label="Avbryt ny kategori"
          >
            ✕
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <Select
        value={override.categoryId ?? '__uncategorized__'}
        disabled={override.skip || transaction.autoConfirmed}
        onValueChange={controls.onCategoryChange}
      >
        <SelectTrigger
          aria-label="Kategori"
          className={isCard ? 'w-full data-[size=default]:h-11' : 'h-7 min-w-36 text-xs'}
          size={isCard ? 'default' : 'sm'}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__uncategorized__">Okategoriserad</SelectItem>
          {categories.map((category) => (
            <SelectItem key={category.id} value={category.id}>
              {category.label}
            </SelectItem>
          ))}
          {onCreateCategory && <SelectItem value="__create__">➕ Ny kategori…</SelectItem>}
        </SelectContent>
      </Select>
    </div>
  );
}

const CREATE_RULE_HINT = 'Skapa kategoriregel från denna transaktion';
const TRANSFER_HINT = 'Markera som intern överföring (exkluderas från budget)';
const TRANSFER_RULE_HINT = 'Skapa överföringsregel (automatiskt i framtida importer)';
const SKIP_HINT = 'Hoppa över transaktionen vid import';

function isCreateRuleDisabled(
  override: TxnOverride,
  transaction: { description: string; amount: number },
) {
  return (
    override.skip ||
    !override.categoryId ||
    override.isTransfer ||
    // Incoming Swish is sorted one at a time, never by a rule
    isIncomingSwish(transaction.description, transaction.amount)
  );
}

// ── Desktop: table row ────────────────────────────────────────────────────────

const ImportPreviewRow = memo(function ImportPreviewRow(props: ImportPreviewRowProps) {
  const { transaction } = props;
  const controls = useImportRowControls(props);

  if (!controls) {
    return null;
  }
  const { override } = controls;

  return (
    <TableRow
      className={`border-b border-border last:border-0 ${rowStateClass(override, transaction)}`}
    >
      <TableCell className="py-2 pl-4 pr-2 text-sm font-mono text-muted-foreground tabular-nums">
        {transaction.date}
      </TableCell>
      <TableCell className="max-w-sm py-2 px-2 text-sm text-foreground">
        {transaction.description}
        <TransactionBadges transaction={transaction} override={override} />
      </TableCell>
      <TableCell
        className={`py-2 px-2 text-right font-mono text-sm ${amountClass(transaction.amount)}`}
      >
        {formatSEK(transaction.amount)}
      </TableCell>
      <TableCell className="py-2 px-2 text-center">
        <ConfidenceBadge transaction={transaction} />
      </TableCell>
      <TableCell className="py-2 px-2 whitespace-normal">
        <CategoryControl props={props} controls={controls} layout="table" />
      </TableCell>
      <TableCell className="py-2 px-2 text-center">
        <div className="flex flex-col items-center gap-1">
          <Checkbox
            checked={override.createRule}
            disabled={isCreateRuleDisabled(override, transaction)}
            title={CREATE_RULE_HINT}
            aria-label={CREATE_RULE_HINT}
            onCheckedChange={controls.onCreateRuleChange}
          />
          {override.createRule && (
            <Button
              type="button"
              onClick={controls.onOpenPattern}
              variant="link"
              size="sm"
              className="h-auto max-w-28 px-0 text-[10px]"
              title={override.rulePattern ?? defaultRulePattern(transaction.description)}
              aria-label={`Ändra regelmönster: ${override.rulePattern ?? defaultRulePattern(transaction.description)}`}
            >
              {override.rulePattern ?? defaultRulePattern(transaction.description)}
            </Button>
          )}
        </div>
      </TableCell>
      <TableCell className="py-2 px-2 text-center">
        <div className="flex items-center justify-center gap-1">
          <Checkbox
            checked={override.isTransfer}
            disabled={override.skip}
            title={TRANSFER_HINT}
            aria-label={TRANSFER_HINT}
            onCheckedChange={controls.onTransferChange}
          />
          {override.isTransfer && (
            <Checkbox
              checked={override.createTransferRule}
              disabled={override.skip}
              title={TRANSFER_RULE_HINT}
              aria-label={TRANSFER_RULE_HINT}
              onCheckedChange={controls.onCreateTransferRuleChange}
            />
          )}
        </div>
      </TableCell>
      <TableCell className="py-2 pr-4 text-center">
        <Checkbox
          checked={override.skip}
          title={SKIP_HINT}
          aria-label={SKIP_HINT}
          onCheckedChange={controls.onSkipChange}
        />
      </TableCell>
    </TableRow>
  );
});

// ── Mobile: card ──────────────────────────────────────────────────────────────

const ImportPreviewCard = memo(function ImportPreviewCard(props: ImportPreviewRowProps) {
  const { transaction } = props;
  const controls = useImportRowControls(props);
  const id = useId();

  if (!controls) {
    return null;
  }
  const { override } = controls;

  return (
    <div
      className={cn(
        'space-y-3 rounded-2xl border border-border bg-card p-3',
        rowStateClass(override, transaction),
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="break-words text-sm font-medium text-foreground">
            {transaction.description}
          </p>
          <p className="mt-0.5 font-mono text-xs text-muted-foreground tabular-nums">
            {transaction.date}
          </p>
          <div className="mt-1 flex flex-wrap gap-y-1 [&>*:first-child]:ml-0">
            <TransactionBadges transaction={transaction} override={override} />
          </div>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <span className={`font-mono text-sm font-semibold ${amountClass(transaction.amount)}`}>
            {formatSEK(transaction.amount)}
          </span>
          <ConfidenceBadge transaction={transaction} />
        </div>
      </div>

      <CategoryControl props={props} controls={controls} layout="card" />

      <div className="flex flex-wrap gap-x-4">
        <label
          htmlFor={`${id}-rule`}
          className="flex min-h-11 items-center gap-2 text-sm text-foreground"
          title={CREATE_RULE_HINT}
        >
          <Checkbox
            id={`${id}-rule`}
            checked={override.createRule}
            disabled={isCreateRuleDisabled(override, transaction)}
            onCheckedChange={controls.onCreateRuleChange}
          />
          Skapa regel
        </label>
        <label
          htmlFor={`${id}-transfer`}
          className="flex min-h-11 items-center gap-2 text-sm text-foreground"
          title={TRANSFER_HINT}
        >
          <Checkbox
            id={`${id}-transfer`}
            checked={override.isTransfer}
            disabled={override.skip}
            onCheckedChange={controls.onTransferChange}
          />
          Överföring
        </label>
        {override.isTransfer && (
          <label
            htmlFor={`${id}-transfer-rule`}
            className="flex min-h-11 items-center gap-2 text-sm text-foreground"
            title={TRANSFER_RULE_HINT}
          >
            <Checkbox
              id={`${id}-transfer-rule`}
              checked={override.createTransferRule}
              disabled={override.skip}
              onCheckedChange={controls.onCreateTransferRuleChange}
            />
            Överföringsregel
          </label>
        )}
        <label
          htmlFor={`${id}-skip`}
          className="flex min-h-11 items-center gap-2 text-sm text-foreground"
          title={SKIP_HINT}
        >
          <Checkbox
            id={`${id}-skip`}
            checked={override.skip}
            onCheckedChange={controls.onSkipChange}
          />
          Hoppa över
        </label>
      </div>

      {override.createRule && (
        <Button
          type="button"
          onClick={controls.onOpenPattern}
          variant="outline"
          className="h-11 w-full justify-start text-xs"
          title={override.rulePattern ?? defaultRulePattern(transaction.description)}
          aria-label={`Ändra regelmönster: ${override.rulePattern ?? defaultRulePattern(transaction.description)}`}
        >
          <span className="shrink-0 text-muted-foreground">Regel:</span>
          <span className="truncate">
            {override.rulePattern ?? defaultRulePattern(transaction.description)}
          </span>
        </Button>
      )}
    </div>
  );
});

interface PatternSelectorBridgeProps {
  store: ImportReviewStoreApi;
  transactions: ImportedTxn[];
  patternForTxnId: string;
  onCancel: () => void;
  onConfirm: (pattern: string) => void;
}

function PatternSelectorBridge({
  store,
  transactions,
  patternForTxnId,
  onCancel,
  onConfirm,
}: PatternSelectorBridgeProps) {
  const activePatternTransaction = useMemo(
    () => transactions.find((transaction) => transaction.id === patternForTxnId) ?? null,
    [patternForTxnId, transactions],
  );
  const currentPattern = useStore(store, (state) => state.overrides[patternForTxnId]?.rulePattern);

  if (!activePatternTransaction) {
    return null;
  }

  return (
    <PatternSelector
      description={activePatternTransaction.description}
      transactions={transactions}
      initialPattern={currentPattern}
      onCancel={onCancel}
      onConfirm={onConfirm}
    />
  );
}
